<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div class="kds-typography-title-medium">
        {{ $t('b2b_self_test_generic_assessment') }}
      </div>
    </template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <h3
          class="kds-typography-title-xlarge"
          v-html="$t('b2b_self_test_activities_question')"
        />
        <div
          class="kds-typography-paragraph-large"
          v-html="$t('b2b_self_test_activities_description')"
        />
      </div>
      <div class="self-test">
        <fieldset
          :disabled="formIsDisabled"
          class="form-container"
        >
          <div
            v-for="fitnessActivity in fitnessActivities"
            :key="fitnessActivity.value"
            :class="[
              'quiz-checkbox',
              { 'quiz-checkbox--checked': fitnessActivity.checked },
            ]"
            @click="
              handleFitnessActivityChange(
                fitnessActivity,
                !fitnessActivity.checked,
              )
            "
          >
            <label class="quiz-checkbox__label kds-typography-title-medium">
              {{ $t(fitnessActivity.label) }}
            </label>
            <div class="quiz-checkbox__layout">
              <custom-checkbox
                :value="fitnessActivity.value"
                :checked="fitnessActivity.checked"
                :data-qa="'fitness-activity-' + fitnessActivity.value"
              />
            </div>
          </div>
        </fieldset>
      </div>
    </div>
    <BaseBanner
      v-if="message.show"
      :variant="message.type"
      :title="message.title"
    >
      <div v-html="$t(message.text)" />
    </BaseBanner>
    <template #actions>
      <BaseButton
        :loading="formIsDisabled"
        :text="interactiveButtonText"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import BaseBanner from '@shared/components/BaseBanner.vue'
import CustomCheckbox from '@shared/components/CustomCheckbox.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import { useI18n } from 'petite-vue-i18n'
import env from '@shared/env.js'
import { computed, ref } from 'vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import { submitSelfTestAnswers } from '@shared/modules/assessment/submitSelfTest.js'

const getDefaultFitnessActivities = (IS_COPD) =>
  IS_COPD
    ? [
        {
          label: 'b2b_self_test_activities_option_dressing',
          value: 'dressing',
        },
        {
          label: 'b2b_self_test_activities_option_walk_100m',
          value: 'walk-100m',
        },
        {
          label: 'b2b_self_test_activities_option_walk_longer_than_100m',
          value: 'walk-longer-than-100m',
        },
        {
          label: 'b2b_self_test_activities_option_walk_uphill',
          value: 'walk-uphill',
        },
        {
          label: 'b2b_self_test_activities_option_strenous_exercise',
          value: 'strenous-exercise',
        },
      ]
    : [
        {
          label: 'b2b_self_test_activities_option_competitive_sports',
          value: 'hard-sports',
        },
        {
          label: 'b2b_self_test_activities_option_recreational_sports',
          value: 'easy-sports',
        },
        {
          label: 'b2b_self_test_activities_option_climbing_stairs',
          value: 'everyday-walking',
        },
        {
          label: 'b2b_self_test_activities_option_household_chores',
          value: 'household-tasks',
        },
      ]

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const { t } = useI18n()
const selfTestStore = useSelfTestStore()

const message = ref({
  text: '',
  title: '',
  type: 'warning',
  show: false,
  code: null,
})
const formIsDisabled = ref(false)

// Seeded from the confirmed answer so a revisit shows the patient's choices.
const confirmed = new Set(
  (selfTestStore.answers.fitness_activities?.answers ?? []).map((a) => a.value),
)
const fitnessActivities = ref(
  getDefaultFitnessActivities(env.isCopd).map((activity) => ({
    ...activity,
    checked: confirmed.has(activity.value),
  })),
)
const interactiveButtonText = computed(() => {
  return fitnessActivities.value.some((a) => a.checked)
    ? t('generic_action_continue')
    : t('b2b_self_test_activities_none_of_these')
})

const handleFitnessActivityChange = (fitnessActivity, checked) => {
  if (formIsDisabled.value) {
    return
  }

  const item = fitnessActivities.value.find(
    (a) => a.value === fitnessActivity.value,
  )
  if (item) item.checked = checked
}

const next = async () => {
  if (formIsDisabled.value) {
    return
  }
  formIsDisabled.value = true

  // The COPD submission below serializes the store, so this answer must be
  // written before it runs.
  const checked = fitnessActivities.value.filter((a) => a.checked)
  if (checked.length) {
    selfTestStore.answer('fitness_activities', {
      answers: checked.map((a) => ({ value: a.value, text: t(a.label) })),
      questionText: t('b2b_self_test_activities_question'),
    })
  } else {
    // "None of these" is a valid way through; the API simply gets no list.
    selfTestStore.clearAnswer('fitness_activities')
  }

  if (env.isCopd) {
    message.value.show = false
    try {
      await submitSelfTestAnswers('full')
    } catch {
      message.value.show = true
      message.value.type = 'warning'
      message.value.title = t('generic__error_title')
      message.value.text = t('generic__error_message', {
        email: env.VITE_SUPPORT_EMAIL,
      })

      formIsDisabled.value = false
      return
    }
  }

  props.handleNextEvent()
}
</script>

<style lang="scss">
@use '@shared/modules/assessment/views/css/SelfTest.scss' as *;
</style>
